import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Chip, Alert, CircularProgress,
  InputAdornment, Tooltip
} from '@mui/material';
import { Add, Search, Edit, Delete, PersonAdd } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EMPTY = { nombre: '', email: '', telefono: '', empresa: '', cargo: '', notas: '' };

export default function Contactos() {
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/crm/contactos`, { headers: headers() });
      setContactos(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando contactos');
    } finally { setLoading(false); }
  }

  function openNew() { setForm(EMPTY); setEditId(null); setDlg(true); }
  function openEdit(c) { setForm({ nombre: c.nombre || '', email: c.email || '', telefono: c.telefono || '', empresa: c.empresa || '', cargo: c.cargo || '', notas: c.notas || '' }); setEditId(c.id); setDlg(true); }

  async function handleSave() {
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/api/crm/contactos/${editId}`, form, { headers: headers() });
      } else {
        await axios.post(`${API}/api/crm/contactos`, form, { headers: headers() });
      }
      setDlg(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando');
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar este contacto?')) return;
    try {
      await axios.delete(`${API}/api/crm/contactos/${id}`, { headers: headers() });
      load();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  const filtered = contactos.filter(c =>
    [c.nombre, c.email, c.telefono, c.empresa].some(f => (f || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Contactos / Leads</Typography>
          <Typography variant="body2" color="text.secondary">{contactos.length} contactos registrados</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openNew}>Nuevo Contacto</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TextField fullWidth placeholder="Buscar por nombre, email, teléfono o empresa..."
        value={search} onChange={e => setSearch(e.target.value)} size="small" sx={{ mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Nombre', 'Email', 'Teléfono', 'Empresa', 'Tipo', 'Acciones'].map(h => (
                <TableCell key={h}><Typography variant="caption" fontWeight={700} color="text.secondary">{h}</Typography></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>Sin contactos</TableCell></TableRow>
            ) : filtered.map(c => (
              <TableRow key={c.id} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{c.nombre}</Typography></TableCell>
                <TableCell><Typography variant="body2">{c.email || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{c.telefono || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{c.empresa || '—'}</Typography></TableCell>
                <TableCell>
                  <Chip label={c.tipo || 'LEAD'} size="small"
                    sx={{ bgcolor: c.tipo === 'CLIENTE' ? '#e8f5e9' : '#fff3e0', color: c.tipo === 'CLIENTE' ? '#2e7d32' : '#e65100', fontSize: 11 }} />
                </TableCell>
                <TableCell>
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Editar Contacto' : 'Nuevo Contacto'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Nombre *" value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} fullWidth />
            <TextField label="Teléfono" value={form.telefono} onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))} fullWidth />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Empresa" value={form.empresa} onChange={e => setForm(p => ({ ...p, empresa: e.target.value }))} fullWidth />
            <TextField label="Cargo" value={form.cargo} onChange={e => setForm(p => ({ ...p, cargo: e.target.value }))} fullWidth />
          </Box>
          <TextField label="Notas" multiline rows={2} value={form.notas} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.nombre || saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
